<template>
  <mdb-modal removeBackdrop size="lg" :show="modalShow" @close="cancel">
    <mdb-modal-header class="text-center" color="indigo">
      <mdb-modal-title tag="h4" bold class="w-100 text-white">
        {{ $t("add_google_purchase_product") }}
      </mdb-modal-title>
    </mdb-modal-header>
    <mdb-modal-body class="mx-3" color="white">
      <mdb-input
        :label="$t('google_product_id')"
        type="text"
        v-model.trim="form.google_id"
        :disabled="loading"
      />
      <small :class="googleIdInvalid ? 'text-danger' : 'text-muted'">
        {{ $t("google_product_id_rules") }}
      </small>
      <mdb-input
        :label="$t('google_product_name')"
        type="text"
        v-model.trim="form.google_name"
        :disabled="loading"
      />
      <mdb-input
        :label="$t('purchaseproduct_internal_name')"
        type="text"
        v-model.trim="form.title"
        :disabled="loading"
      />

      <div class="form-row align-items-end">
        <div class="col-md-6">
          <mdb-input
            :label="$t('purchaseproduct_price_eur')"
            type="number"
            :min="0.01"
            :step="0.01"
            v-model="form.price_eur"
            :disabled="loading"
          />
        </div>
        <div class="col-md-3">
          <mdb-input
            :label="$t('billing_period_value')"
            type="number"
            :min="1"
            :step="1"
            v-model.number="form.billing_period_value"
            :disabled="loading"
          />
        </div>
        <div class="col-md-3 pb-3">
          <label for="billing-period-unit">{{ $t("period_unit") }}</label>
          <select
            id="billing-period-unit"
            class="browser-default custom-select"
            v-model="form.billing_period_unit"
            :disabled="loading"
          >
            <option v-for="unit in periodUnits" :key="unit" :value="unit">
              {{ $t(`period_unit_${unit}`) }}
            </option>
          </select>
        </div>
      </div>

      <div class="custom-control custom-switch my-3">
        <input
          id="free-trial-enabled"
          class="custom-control-input"
          type="checkbox"
          v-model="form.free_trial_enabled"
          :disabled="loading"
        />
        <label class="custom-control-label" for="free-trial-enabled">
          {{ $t("free_trial_enabled") }}
        </label>
      </div>

      <template v-if="form.free_trial_enabled">
        <div class="form-row align-items-end">
          <div class="col-md-6">
            <mdb-input
              :label="$t('free_trial_value')"
              type="number"
              :min="1"
              :step="1"
              v-model.number="form.free_trial_value"
              :disabled="loading"
            />
          </div>
          <div class="col-md-6 pb-3">
            <label for="free-trial-unit">{{ $t("period_unit") }}</label>
            <select
              id="free-trial-unit"
              class="browser-default custom-select"
              v-model="form.free_trial_unit"
              :disabled="loading"
            >
              <option v-for="unit in periodUnits" :key="unit" :value="unit">
                {{ $t(`period_unit_${unit}`) }}
              </option>
            </select>
          </div>
        </div>
        <mdb-input
          :label="$t('store_promocode_optional')"
          type="text"
          :maxlength="50"
          v-model.trim="form.promocode"
          :disabled="loading"
        />
      </template>

      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{ serverError }}
      </div>
    </mdb-modal-body>
    <mdb-modal-footer center>
      <mdb-btn color="secondary" :disabled="loading" @click="cancel">
        {{ $t("cancel") }}
      </mdb-btn>
      <mdb-btn color="indigo" :disabled="!canSubmit || loading" @click="submit">
        <mdb-icon :icon="loading ? 'spinner' : 'plus'" :spin="loading" class="mr-1" />
        {{ $t(loading ? "creating_google_product" : "create_google_product") }}
      </mdb-btn>
    </mdb-modal-footer>
  </mdb-modal>
</template>

<script>
import {
  mdbBtn,
  mdbIcon,
  mdbInput,
  mdbModal,
  mdbModalBody,
  mdbModalFooter,
  mdbModalHeader,
  mdbModalTitle,
} from "mdbvue";

const emptyForm = () => ({
  google_id: "",
  google_name: "",
  title: "",
  price_eur: "",
  billing_period_value: 1,
  billing_period_unit: "month",
  free_trial_enabled: false,
  free_trial_value: 1,
  free_trial_unit: "week",
  promocode: "",
});

export default {
  name: "AddGooglePurchaseProduct",
  props: {
    modalShow: Boolean,
  },
  components: {
    mdbBtn,
    mdbIcon,
    mdbInput,
    mdbModal,
    mdbModalBody,
    mdbModalFooter,
    mdbModalHeader,
    mdbModalTitle,
  },
  data() {
    return {
      endpoint: "/1.0/front/purchaseproducts/google",
      form: emptyForm(),
      loading: false,
      serverError: "",
      periodUnits: ["day", "week", "month", "year"],
    };
  },
  computed: {
    googleIdInvalid() {
      return this.form.google_id.length > 0 &&
        !/^[a-z0-9][a-z0-9._]{0,39}$/.test(this.form.google_id);
    },
    canSubmit() {
      const price = Number(String(this.form.price_eur).replace(",", "."));
      const trialValid = !this.form.free_trial_enabled || this.form.free_trial_value >= 1;
      const promocodeValid = !this.form.promocode ||
        (this.form.promocode.length <= 50 && this.form.free_trial_enabled);
      return !this.googleIdInvalid &&
        this.form.google_id.length > 0 &&
        this.form.google_name.length > 0 &&
        this.form.google_name.length <= 100 &&
        this.form.title.length > 0 &&
        this.form.title.length <= 50 &&
        this.form.billing_period_value >= 1 &&
        Number.isFinite(price) && price > 0 && trialValid && promocodeValid;
    },
  },
  watch: {
    modalShow(isVisible) {
      if (isVisible) this.reset();
    },
    form: {
      deep: true,
      handler(form) {
        if (!form.free_trial_enabled && form.promocode) form.promocode = "";
      },
    },
  },
  methods: {
    reset() {
      this.form = emptyForm();
      this.serverError = "";
      this.loading = false;
    },
    cancel() {
      if (!this.loading) this.$emit("cancelled");
    },
    submit() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;
      this.serverError = "";
      const payload = {
        ...this.form,
        sessionid: this.$store.getters.getToken,
        price_eur: String(this.form.price_eur).replace(",", "."),
      };
      this.$http.post(this.endpoint, payload)
        .then((response) => {
          this.$notify.success({
            message: this.$t("google_product_create_success"),
            position: "top right",
            timeOut: this.$store.state.notifytimeoutshort,
          });
          this.$emit("saved", response.data.purchaseproductdata);
        })
        .catch((error) => {
          this.serverError = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : this.$t("google_product_create_error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>